import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { IInterval } from '@/types/common/IInterval';
import { Box, Card, CardBody, Fade, Flex, Grid, Tag, TagRightIcon, Text, Tooltip } from '@chakra-ui/react';
import { t } from 'i18next';
import React, { useRef, useState } from 'react';
import { FiUsers } from 'react-icons/fi';
import { Temporal } from 'temporal-polyfill';

interface IProps {
	event: ICalendarEvent;
	onClick: (event: ICalendarEvent) => void;
	borderMode: 'EVENT_STATUS' | 'ATTENDEE_STATUS';
	displayDotOnPendingEvents: boolean;
}

export default function EventSummary(props: IProps) {
	const borderLeftColor =
		props.borderMode === 'EVENT_STATUS'
			? _getBorderColorFromEventStatus(props.event)
			: _getBorderColorFromAttendeeStatus(props.event);

	const [isDragging, setIsDragging] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null); // Ref para el elemento de arrastre

	const handleDragStart = (e: React.DragEvent) => {
		e.dataTransfer.setData('text/plain', JSON.stringify(props.event));
		// Agrega la clase para la animación
		setIsDragging(true);
	};

	const handleDragEnd = () => {
		// Wait for the server to respond to prevent flickering
		setTimeout(() => {
			if (cardRef.current) {
				cardRef.current.removeAttribute('style'); // Limpiar estilos
			}
			setIsDragging(false);
		}, 300);
	};

	return (
		<Fade in={true}>
			<Card
				ref={cardRef}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				draggable
				borderLeftColor={borderLeftColor}
				borderLeftWidth={6}
				cursor="pointer"
				borderRadius="sm"
				onClick={() => props.onClick(props.event)}
				transformOrigin="center"
				transition="all .1s"
				opacity={isDragging ? 0 : 1}
			>
				<CardBody display="grid" gridTemplateColumns="1fr">
					<Grid gridTemplateColumns="1fr auto" alignItems="center" mb={1}>
						<Grid gridTemplateColumns="1fr auto" alignItems="center" gap={2}>
							<Flex alignItems="center" gap={2}>
								{props.event.status === 'TENTATIVE' ? (
									<Box
										display={props.displayDotOnPendingEvents ? 'block' : 'none'}
										h={2}
										w={2}
										backgroundColor="primary.500"
										borderRadius="full"
									/>
								) : null}
								<Text fontSize="small" fontWeight={500} color="gray.500">
									{getDateString(props.event.interval)}
								</Text>
							</Flex>

							<Grid>
								<Tooltip label={t('Confirmed attendees / Total attendees')}>
									<Tag variant="solid" size="sm" colorScheme={_getTagColorScheme(props.event)}>
										<Text>{props.event.attendees.filter((a) => a.status === 'ACCEPTED').length}</Text>/
										<Text>{props.event.attendees.length}</Text>
										<TagRightIcon as={FiUsers} />
									</Tag>
								</Tooltip>
							</Grid>
						</Grid>
					</Grid>

					<Text fontWeight={700}>{props.event.summary}</Text>
					<Text fontSize="small" color="gray.600">
						{props.event.description}
					</Text>
				</CardBody>
			</Card>
		</Fade>
	);
}

function getDateString(interval: IInterval | null): string {
	if (!interval) {
		return t('No date');
	}

	const start = Temporal.ZonedDateTime.from(`${interval.inclusiveStart.dateTime}[${interval.inclusiveStart.timeZone}]`);
	const end = Temporal.ZonedDateTime.from(`${interval.exclusiveEnd.dateTime}[${interval.exclusiveEnd.timeZone}]`);

	return `
	${start.hour.toString().padStart(2, '0')}:${start.minute.toString().padStart(2, '0')} - ${end.hour
		.toString()
		.padStart(2, '0')}:${end.minute.toString().padStart(2, '0')}`;
}

function _getBorderColorFromAttendeeStatus(event: ICalendarEvent): string {
	return `${_getTagColorScheme(event)}.300`;
}

function _getBorderColorFromEventStatus(event: ICalendarEvent) {
	if (event.status === 'TENTATIVE') {
		return 'gray.300';
	}

	if (event.status === 'CONFIRMED') {
		return 'green.300';
	}

	if (event.status === 'CANCELLED') {
		return 'red.300';
	}

	return 'gray.300';
}

function _getTagColorScheme(event: ICalendarEvent): string {
	if (!event.attendees.length) {
		return 'gray';
	}

	if (event.attendees.every((a) => a.status === 'ACCEPTED')) {
		return 'green';
	}

	if (event.attendees.some((a) => a.status === 'DECLINED')) {
		return 'red';
	}

	return 'gray';
}
